import {
    Button,
    CircularProgress,
    Container,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDataProvider, useNotify } from 'react-admin';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Navigate, Link } from 'react-router-dom';
import { IrmDataProvider } from '../../providers/types';
import {
    useConfigurationContext,
    useThemeLogo,
} from '../../root/ConfigurationContext';
import { NewsletterSubscription } from '../../types/types';
import { getAppStatus } from '../../providers/firebase';
import { FooterLinks } from '../../components/FooterLinks';

export const SignupPage = () => {
    const dataProvider = useDataProvider<IrmDataProvider>();
    const { title } = useConfigurationContext();
    const logo = useThemeLogo();
    const notify = useNotify();

    const { data: appStatus } = useQuery({
        queryKey: ['app_status'],
        queryFn: getAppStatus,
    });

    const {
        register,
        handleSubmit,
        formState: { isValid },
        reset,
    } = useForm<NewsletterSubscription>({ mode: 'onChange' });

    const { isPending, mutate, isSuccess } = useMutation({
        mutationKey: ['newsletter-subscribe'],
        mutationFn: async (data: NewsletterSubscription) =>
            dataProvider.subscribeNewsletter(data),
        onSuccess: () => {
            notify('Thanks — you are subscribed.');
            reset();
        },
        onError: () => {
            notify('Something went wrong. Please try again.', { type: 'error' });
        },
    });

    const onSubmit: SubmitHandler<NewsletterSubscription> = data => mutate(data);

    // Keep this below every hook: the status arrives after the first render, and
    // returning earlier changes the hook count between renders.
    if (appStatus?.app_locked) {
        return <Navigate to="/maintenance" />;
    }

    return (
        <Stack sx={{ height: '100dvh', p: 2 }}>
            <Stack direction="row" alignItems="center" gap={1}>
                <Link to="/">
                    <img src={logo} alt={title} width={200} style={{ cursor: 'pointer' }} />
                </Link>
            </Stack>
            <Stack sx={{ height: '100%' }}>
                <Container
                    maxWidth="xs"
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 1,
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Stay in the loop
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Subscribe to occasional updates from {title}. Account
                        access is invite-only; contact your administrator if
                        you need access.
                    </Typography>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField
                                {...register('first_name')}
                                label="First name (optional)"
                                variant="outlined"
                            />
                            <TextField
                                {...register('last_name')}
                                label="Last name (optional)"
                                variant="outlined"
                            />
                            <TextField
                                {...register('email', { required: true })}
                                label="Email"
                                type="email"
                                variant="outlined"
                                required
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!isValid || isPending}
                                fullWidth
                            >
                                {isPending ? <CircularProgress size={20} /> : 'Subscribe'}
                            </Button>
                            {isSuccess && (
                                <Typography variant="body2" color="success.main">
                                    Subscribed.
                                </Typography>
                            )}
                        </Stack>
                    </form>
                </Container>
            </Stack>
            <FooterLinks extraLinks={[{ label: 'Login', to: '/login' }]} />
        </Stack>
    );
};

SignupPage.path = '/signup';
